import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import _isFunction from 'lodash/isFunction';

function Form(props) {
  const { children, debug = false, defaultValues = {}, onSubmit, onReset, useFormOverrides = {}, ...restProps } = props;

  const methods = useForm({
    defaultValues,
    criteriaMode: 'all',
    ...useFormOverrides,
  });
  const { setError, reset, handleSubmit, control } = methods;

  const devToolsPortalTarget = document.getElementById('rhf-dev-tools-host');
  const devToolsContent = <DevTool control={control} />;
  const showDevTools = process.env.NODE_ENV !== 'production' && debug && devToolsPortalTarget;

  function handleReset(e) {
    e.preventDefault();
    e.stopPropagation();
    reset(defaultValues);
    if (onReset) onReset();
  }

  // pass relevant form instance methods to the async handler
  const submit = handleSubmit(onSubmit({ setError, reset }));

  return (
    <FormProvider {...methods}>
      <form onSubmit={submit} onReset={handleReset} noValidate {...restProps}>
        {!_isFunction(children) ? children : children({ ...methods, submit })}
      </form>
      {showDevTools && ReactDOM.createPortal(devToolsContent, devToolsPortalTarget)}
    </FormProvider>
  );
}

Form.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  debug: PropTypes.bool,
  defaultValues: PropTypes.shape({}),
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  useFormOverrides: PropTypes.shape({}),
};

function FormWrapper({ isLoading, ...restProps }) {
  /** If the initial values data aren't loaded when the form first renders, the initial values will be undefined.
   * This means that we'll have to manually setValue for each field once the form values are loaded. Setting isLoading
   * lets the caller delay form initialization until all of the initial values are present.
   * */
  if (isLoading) return null;
  return <Form {...restProps} />;
}

FormWrapper.propTypes = {
  isLoading: PropTypes.bool,
};

export default FormWrapper;
